import { Injectable } from 'framework/di';

import { NavigationHelper } from '../../helpers/navigation.helper';
import { renderSupportButton } from '../../integration/support-crm';
import type { TCustomerSupportProvider } from '../../types/thirdPartyAppSettings';
import { AppService } from '../app/app.service';
import { InstantSearchAPI } from './instant-search.api';
import { InstantSearchSelectors } from './instant-search.selectors';
import { InstantSearchService } from './instant-search.service';
import type {
  DefaultSuggestionData,
  ISWState,
  SearchSettings,
  SuggestionData,
} from './instant-search.types';
import { PositionService } from './position.service';
import { RecentSearchService } from './recent-search.service';

const NAVIGATION_KEYS = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab'];

// CSS class for selected suggestion item
const SELECTED_CLASS = 'boost-sd__suggestion-queries-item-selected';

const ACTIVATION_KEYS = ['Enter', ' ', 'Spacebar'];

// localStorage key for clicked suggestion term (AIML analytics)
const CLICK_SUGGESTION_TERM_KEY = 'boostSDClickSuggestionTerm';

//Id of the ARIA live region used to announce results to screen readers
const LIVE_REGION_ID = 'boost-sd__isw-live-region';

/**
 * Controller for instant search UI interactions.
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('InstantSearchController', (InstantSearchController) => {
 *   return class CustomController extends InstantSearchController {
 *     // Add custom analytics on open
 *     async openSuggestion(input) {
 *       this.trackOpen();
 *       await super.openSuggestion(input);
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class InstantSearchController {
  protected state: ISWState = {
    isOpen: false,
    isLoading: false,
    currentQuery: '',
    keyboardNavIndex: -1,
    activeInputId: null,
  };

  /** Whether controller is initialized */
  protected initialized = false;

  /** Prevent concurrent renders */
  protected isRendering = false;

  /** Cached ISW template */
  protected template: string = '';

  /** Default suggestion data */
  protected defaultData: DefaultSuggestionData | null = null;

  /** Current bundles from the latest render (for keyboard Enter handling) */
  protected currentBundles: Array<Record<string, unknown>> = [];

  /** Search settings cache */
  protected searchSettings: SearchSettings | null = null;

  /** Bound event handlers for cleanup */
  protected boundHandlers: Map<string, EventListener> = new Map();

  /** Saved focus element for restoration */
  protected savedFocusElement: HTMLElement | null = null;

  /** Guard flag to prevent ISW from reopening during a close cycle */
  protected isClosing = false;

  /** Original query typed by the user before keyboard navigation started */
  protected originalQuery: string = '';

  /** Scroll handler cleanup function from position service */
  protected scrollCleanup: (() => void) | null = null;

  /** Previous mobile state for responsive breakpoint change detection */
  protected previousIsMobile: boolean | null = null;

  /** MutationObserver for dialog integration cleanup */
  protected dialogObserver: MutationObserver | null = null;

  /** Focus-trap keydown handler stored for cleanup */
  protected focusTrapHandler: ((e: KeyboardEvent) => void) | null = null;

  /** Delayed focus timer from openFullWidth — must be canceled on close/destroy */
  protected focusDelayTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    protected appService: AppService,
    protected instantSearchService: InstantSearchService,
    protected instantSearchAPI: InstantSearchAPI,
    protected instantSearchSelectors: InstantSearchSelectors,
    protected positionService: PositionService,
    protected recentSearchService: RecentSearchService
  ) {}

  /**
   * Get current ISW state.
   */
  getState(): Readonly<ISWState> {
    return { ...this.state };
  }

  /**
   * Get current suggestion data.
   */
  getSuggestionData(): SuggestionData | null {
    return this.defaultData;
  }

  /**
   * Initialize controller without context (basic setup).
   *
   * **Legacy**: Part of handleInstantSearchWidget()
   * **New**: Separated initialization method
   */
  init(): void {
    if (this.initialized) {
      this.appService.logger.warn('Instant search controller already initialized');
      return;
    }

    this.setupGlobalEventListeners();
    this.initialized = true;
  }

  /**
   * Initialize controller with full context.
   *
   * **Legacy**: initInstantSearchWidget(context) + initInstantSearchWidgetEvents(context)
   * **New**: initWithContext(searchSettings) with proper typing
   *
   * @param searchSettings - Search settings
   */
  async initWithContext(searchSettings: SearchSettings): Promise<void> {
    // Guard: if already initialized, only rebind search inputs (for dynamically
    // added inputs after SPA navigations or theme section re-renders).
    if (this.initialized) {
      this.searchSettings = searchSettings;
      this.bindSearchInputs();
      return;
    }

    this.searchSettings = searchSettings;

    this.bindSearchInputs();

    // 1. Fetch template
    const templateId = (this.appService.TAEAppConfig.templateMetadata as Record<string, string>)
      ?.instantSearchWidget;
    if (templateId) {
      this.template = await this.instantSearchAPI.getTemplate(templateId);
    }

    // 2. Build default data
    this.defaultData = await this.instantSearchService.getDefaultSuggestionData(searchSettings);

    // 3. Initial render — populate #bc-instant-search with the template + default data
    //    (Legacy: setInstantSearch(defaultData) triggers context.render → context.document.innerHTML = html)
    //    This must happen before dialog integration so the ISW DOM structure exists.
    await this.renderDefaultData();

    // 3a. Sync the mobile style from the rendered DOM into searchSettings.
    //     The admin API no longer returns suggestionMobileStyle, so Liquid templates
    //     can override it (e.g. {% assign suggestionMobileStyle = 'style2' %}).  After
    //     the initial render, detect which HTML structure was produced and update
    //     this.searchSettings so that isStyleFullWidth / calculateMaxHeight agree with
    //     the actual DOM layout.
    const detectedMobileStyle = this.detectMobileStyleFromDOM();
    if (detectedMobileStyle && detectedMobileStyle !== this.searchSettings!.suggestionMobileStyle) {
      this.searchSettings = { ...this.searchSettings!, suggestionMobileStyle: detectedMobileStyle };
      if (this.defaultData) {
        this.defaultData = { ...this.defaultData, suggestionMobileStyle: detectedMobileStyle };
      }
      this.appService.logger.info(`[ISW] Mobile style detected from DOM: ${detectedMobileStyle}`);
    }

    // Apply shop-specific widget styles now that the container element is in the DOM.
    const styleContainer = document.querySelector<HTMLElement>(
      '.boost-sd__instant-search-container'
    );
    this.applyWidgetStyles(styleContainer || document.body);

    // 4. Bind search inputs
    this.bindSearchInputs();

    const urlParams = new URLSearchParams(window.location.search);
    const initialQuery = urlParams.get('q');
    if (initialQuery) {
      if (this.containsHtml(initialQuery)) {
        // XSS detected — full reload to /search so server-rendered content
        window.location.replace(window.location.pathname);
        return;
      } else {
        void this.searchAndRender(this.stripHtml(initialQuery));
      }
    }

    // 5. Setup event listeners (global + custom window events)
    this.setupEventListeners();

    // 6. Handle dialog integration for dropdown styles
    this.handleDialogIntegration();

    // 8. Add enabled class
    document.body.classList.add('boost-sd__search-widget-init-enabled');

    this.initialized = true;
    this.appService.logger.info('Instant search controller initialized');
  }

  /**
   * Bind event handlers to search inputs.
   *
   * **Legacy**: Part of initInstantSearchWidgetEvents()
   * **New**: Separated method for rebinding
   */
  bindSearchInputs(): void {
    const inputs = this.instantSearchSelectors.getAllSearchInputs();

    inputs.forEach((input, index) => {
      this.setupInputAttributes(input, index);
      this.setupAccessibility(input);

      if (!input.dataset.boostEventBound) {
        this.bindInputEvents(input);
        input.dataset.boostEventBound = 'true';
      }
    });
  }

  /**
   * Setup input element attributes.
   *
   * @param input - Input element
   * @param index - Input index
   */
  protected setupInputAttributes(input: HTMLInputElement, index: number): void {
    const inputId = `boost-sd__search-widget-init-input-${index}`;
    input.id = inputId;
    input.classList.add('boost-sd__search-widget-init-input');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('maxlength', '150');

    if (!input.dataset.boostEventBound) {
      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get('q');
      if (query && !this.containsHtml(query)) {
        input.value = this.stripHtml(query);
      }
    }
  }

  /**
   * Setup accessibility attributes.
   *
   * **Legacy**: Inline in initInstantSearchWidgetEvents()
   * **New**: Dedicated method for WCAG compliance
   *
   * @param input - Input element
   */
  setupAccessibility(input: HTMLInputElement): void {
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', 'boost-sd__search-widget-init-wrapper');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-owns', 'boost-sd__search-widget-init-wrapper');
  }

  /**
   * Bind event handlers to an input.
   *
   * @param input - Input element
   */
  protected bindInputEvents(input: HTMLInputElement): void {
    // Input change handler
    input.addEventListener('input', (e) => {
      this.handleInputChange(e, input);
      this.ensureMobileWrapperVisible();
    });

    // Click handler
    input.addEventListener('click', (e) => {
      this.handleInputClick(e, input);
      this.ensureMobileWrapperVisible();
    });

    // Focus handler
    input.addEventListener('focusin', (e) => {
      this.ensureMobileWrapperVisible();
      this.handleInputFocus(e, input);
    });

    // Keydown handler
    if (!window.boostSdDisableISWKeyDown) {
      input.addEventListener('keydown', (e) => {
        this.handleKeydown(e, input);
      });
    }

    // Setup form buttons
    this.setupFormButtons(input);
  }

  /**
   * Setup form buttons (submit, reset, close).
   *
   * @param input - Input element
   */
  protected setupFormButtons(input: HTMLInputElement): void {
    const form = input.closest('form');

    if (form) {
      // Submit button
      const submitButton = form.querySelector('[type="submit"], .search__button');
      if (submitButton) {
        submitButton.addEventListener('click', (e) => {
          this.handleSubmit(e, input);
        });

        // Keydown handler for WCAG: track keyboard-activated search submissions
        submitButton.addEventListener('keydown', (e) => {
          this.handleSubmitButtonKeydown(e as KeyboardEvent, submitButton as HTMLElement);
        });
      }

      // Reset button
      const resetButton = form.querySelector(
        '[type="reset"], .reset__button, .boost-sd__button-text-x--clear'
      );
      if (resetButton) {
        resetButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleReset(input, resetButton as HTMLElement);
        });

        // Show reset button if query exists
        const query = new URLSearchParams(window.location.search).get('q');
        if (query) {
          resetButton.classList.remove('boost-sd__g-hide');
        }
      }
    }

    // Close button — search from the form first, then fall back to the
    // ISW container. The close button may live outside the <form> element
    // (e.g., the ISW template wraps the search bar in a <div>, not a <form>).
    const closeButtonHost =
      form ||
      input.closest('.boost-sd__instant-search-container-mobile') ||
      input.closest('.boost-sd__instant-search-container-desktop') ||
      input.closest('.boost-sd__instant-search-container');
    const closeButton = closeButtonHost?.querySelector<HTMLElement>(
      '.boost-sd__button-text-x--close'
    );
    if (closeButton && !closeButton.dataset.boostCloseBound) {
      closeButton.addEventListener('click', () => {
        this.closeSuggestion();
      });
      closeButton.dataset.boostCloseBound = 'true';
    }
  }

  /**
   * Handle input change event.
   *
   * **Legacy**: handleSearchInputChange(context, e, input) + searchInputListener()
   * **New**: handleInputChange(e, input) with state management
   *
   * @param event - Input event
   * @param input - Input element
   */
  handleInputChange(event: Event, input: HTMLInputElement): void {
    event.preventDefault();

    const value = (event.target as HTMLInputElement).value;
    this.state.currentQuery = value;
    this.state.keyboardNavIndex = -1;

    // Sync all inputs
    this.syncAllInputs(value);

    // Toggle reset button visibility
    this.toggleResetButton(input, value.length > 0);

    // Remove current selection
    this.removeCurrentSelected();

    // Trigger search or show default data
    if (value === '') {
      // Cancel any pending debounce timer and abort any in-flight request so
      // the empty-input state is not overwritten by a stale search response.
      this.instantSearchService.cancelSearch();

      if (input.hasAttribute('data-disable-open-on-empty')) {
        // The input was cleared back to empty — close the ISW.
        // Mirrors the click/focus guard: an empty input with
        // data-disable-open-on-empty should never show the suggestion panel.
        this.closeSuggestion(input);
      } else {
        void this.openSuggestion(input);
        this.renderDefaultData();
      }
    } else {
      // Open suggestion panel when user types (legacy: searchInputListener on input event)
      void this.openSuggestion(input);
      this.searchAndRender(value);
    }
  }

  /**
   * Handle input click event.
   *
   * Respects `data-disable-open-on-empty`: when this attribute is present on the
   * input element, clicking an empty search box will not open the suggestion
   * panel. The panel still opens normally once the user starts typing.
   *
   * @example
   * ```html
   * <input name="q" data-disable-open-on-empty />
   * ```
   *
   * @param event - Click event
   * @param input - Input element
   */
  async handleInputClick(event: Event, input: HTMLInputElement): Promise<void> {
    if (input.hasAttribute('data-disable-open-on-empty') && !input.value.trim()) return;

    await this.openSuggestion(input);

    // Recalculate position on every click (legacy runs calcPositionSuggestionResult
    // on every click event regardless of open state)
    if (this.state.isOpen && this.searchSettings) {
      const isFullWidth = this.instantSearchSelectors.isFullWidthStyle(this.searchSettings);
      if (!isFullWidth) {
        this.updatePosition(input);
      }
    }
  }

  /**
   * Handle input focus event.
   *
   * Respects `data-disable-open-on-empty`: when this attribute is present on the
   * input element, focusing an empty search box will not open the suggestion
   * panel. The panel still opens normally once the user starts typing.
   *
   * @param event - Focus event
   * @param input - Input element
   */
  async handleInputFocus(event: Event, input: HTMLInputElement): Promise<void> {
    if (input.hasAttribute('data-disable-open-on-empty') && !input.value.trim()) return;

    await this.openSuggestion(input);

    // Recalculate position on every focus (legacy runs calcPositionSuggestionResult
    // on every focusin event regardless of open state)
    if (this.state.isOpen && this.searchSettings) {
      const isFullWidth = this.instantSearchSelectors.isFullWidthStyle(this.searchSettings);
      if (!isFullWidth) {
        this.updatePosition(input);
      }
    }
  }

  /**
   * Handle keydown event for keyboard navigation.
   *
   * **Legacy**: searchInputKeydownListener(context, e, input)
   * **New**: handleKeydown(e, input) with proper state management
   *
   * @param event - Keyboard event
   * @param input - Input element
   */
  handleKeydown(event: KeyboardEvent, input: HTMLInputElement): void {
    if (!NAVIGATION_KEYS.includes(event.key)) return;
    if (!this.state.isOpen) return;

    // Save original typed query when keyboard navigation first starts from rest position
    if (
      (event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
      this.state.keyboardNavIndex === -1
    ) {
      this.originalQuery = input.value;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigateDown(input);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.navigateUp(input);
        break;

      case 'Enter':
        event.preventDefault();
        this.handleEnter(input);
        break;

      case 'Escape':
        event.preventDefault();
        this.closeSuggestion(input);
        input.focus();
        break;

      case 'Tab':
        this.handleTab(event);
        break;
    }
  }

  /**
   * Navigate down in suggestions.
   *
   * @param input - Input element
   */
  protected navigateDown(input: HTMLInputElement): void {
    const items = this.getNavigableItems();
    const maxIndex = items.length - 1;

    this.state.keyboardNavIndex++;
    if (this.state.keyboardNavIndex > maxIndex) {
      this.state.keyboardNavIndex = -1; // Back to input
    }

    this.updateSelection(items, input);
  }

  /**
   * Navigate up in suggestions.
   *
   * @param input - Input element
   */
  protected navigateUp(input: HTMLInputElement): void {
    const items = this.getNavigableItems();
    const maxIndex = items.length - 1;

    this.state.keyboardNavIndex--;
    if (this.state.keyboardNavIndex < -1) {
      this.state.keyboardNavIndex = maxIndex;
    }

    this.updateSelection(items, input);
  }

  /**
   * Update selection state and UI.
   *
   * @param items - Suggestion items
   * @param input - Input element
   */
  protected updateSelection(items: HTMLElement[], input: HTMLInputElement): void {
    // Remove previous selection
    items.forEach((item) => {
      item.classList.remove(SELECTED_CLASS);
      item.setAttribute('aria-selected', 'false');
      item.removeAttribute('id');
    });

    if (this.state.keyboardNavIndex >= 0 && this.state.keyboardNavIndex < items.length) {
      const current = items[this.state.keyboardNavIndex];
      current.classList.add(SELECTED_CLASS);
      current.setAttribute('aria-selected', 'true');
      current.id = 'boost-sd__suggestion-queries-item-selected';

      // Update input aria-activedescendant
      input.setAttribute('aria-activedescendant', 'boost-sd__suggestion-queries-item-selected');

      // Update input value with suggestion title (preserve original typed query in state)
      const title = current.getAttribute('data-title');
      if (title) {
        input.value = title;
      }

      // Scroll into view
      current.scrollIntoView({ block: 'nearest' });
    } else {
      // Restore original typed query when navigation wraps back to rest position
      input.removeAttribute('aria-activedescendant');
      input.value = this.originalQuery || this.state.currentQuery;
    }
  }

  /**
   * Handle Enter key press.
   *
   * @param input - Input element
   */
  protected handleEnter(input: HTMLInputElement): void {
    // If a navigable item is selected, activate it (click or navigate)
    if (this.state.keyboardNavIndex >= 0) {
      const items = this.getNavigableItems();
      const current = items[this.state.keyboardNavIndex];

      if (current) {
        // Handle bundle items via custom events (DI-based controllers listen for these)
        if (this.handleBundleEnter(current)) {
          return;
        }

        const href = current.querySelector('a')?.href || current.getAttribute('data-href');

        if (href && NavigationHelper.isSafeUrl(href)) {
          // Save to recent searches before navigating
          const query = input.value.trim();
          if (query) {
            this.recentSearchService.addRecentSearch(query);
          }
          window.location.href = href;
        } else {
          // Click the element (clear/close/view-all buttons)
          current.click();
        }
        return;
      }
    }

    const rawValue = input.value.trim();
    const query = this.stripHtml(rawValue);
    if (!query) {
      // Raw input had content but stripped to empty (HTML tags only) → redirect to /search?q=
      if (rawValue) {
        window.location.replace(this.instantSearchService.getSearchPageUrl(''));
      }
      return;
    }

    // Save to recent searches
    this.recentSearchService.addRecentSearch(query);

    // Check for redirect
    const redirects = this.defaultData?.redirects || {};
    const redirectUrl = this.instantSearchService.checkRedirect(query, redirects);

    if (redirectUrl && NavigationHelper.isSafeUrl(redirectUrl)) {
      window.location.href = redirectUrl;
    } else {
      // Navigate to search page
      window.location.href = this.instantSearchService.getSearchPageUrl(query);
    }
  }

  /**
   * Handle Enter on bundle items by dispatching events to DI-based controllers.
   *
   * @param current - The currently selected navigable item
   * @returns True if a bundle event was dispatched
   */
  protected handleBundleEnter(current: HTMLElement): boolean {
    const isEmbeddedBundle = current.classList.contains(
      'boost-sd__suggestion-queries-item--embedded-bundle'
    );
    const isBottomBundle = current.classList.contains(
      'boost-sd__suggestion-queries-item--bottom-bundle'
    );
    const isVolumeBundle = current.classList.contains(
      'boost-sd__suggestion-queries-item--volume-bundle'
    );

    if (isEmbeddedBundle || isBottomBundle) {
      const dynamicWidgetId = current
        .querySelector('.boost-sd__recommendation--dynamic, .boost-sd__product-bundle')
        ?.getAttribute('data-widget-id');
      if (dynamicWidgetId) {
        const bundleData = this.currentBundles.find((b) => b.widgetId === dynamicWidgetId);
        window.dispatchEvent(
          new CustomEvent('boost-sd-open-dynamic-bundle', {
            detail: { widgetId: dynamicWidgetId, bundleData, blockType: 'instantSearch' },
          })
        );
        return true;
      }
    }

    if (isVolumeBundle) {
      const volumeWidgetId = current
        .querySelector('.boost-sd__recommendation--volume, .boost-sd__volume-bundle-product-item')
        ?.getAttribute('data-widget-id');
      if (volumeWidgetId) {
        window.dispatchEvent(
          new CustomEvent('boost-sd-open-volume-bundle', {
            detail: { widgetId: volumeWidgetId },
          })
        );
        return true;
      }
    }

    return false;
  }

  /**
   * Handle Tab key press.
   *
   * @param event - Keyboard event
   */
  protected handleTab(event: KeyboardEvent): void {
    // Allow natural tab behavior but close on tab out
    setTimeout(() => {
      const iswElement = document.querySelector('.boost-sd__search-widget-init-wrapper');
      if (iswElement && !iswElement.contains(document.activeElement)) {
        this.closeSuggestion();
      }
    }, 100);
  }

  /**
   * Handle form submit.
   *
   * @param event - Click event
   * @param input - Input element
   */
  protected handleSubmit(event: Event, input: HTMLInputElement): void {
    event.preventDefault();

    const rawValue = input.value.trim();
    const query = this.stripHtml(rawValue);
    if (!query) {
      if (rawValue) {
        window.location.replace(this.instantSearchService.getSearchPageUrl(''));
      }
      return;
    }

    // Save to recent searches
    this.recentSearchService.addRecentSearch(query);

    // Check for redirect
    const redirects = this.defaultData?.redirects || {};
    const redirectUrl = this.instantSearchService.checkRedirect(query, redirects);

    if (redirectUrl && NavigationHelper.isSafeUrl(redirectUrl)) {
      window.location.href = redirectUrl;
    } else {
      window.location.href = this.instantSearchService.getSearchPageUrl(query);
    }
  }

  /**
   * Handle reset button click.
   *
   * @param input - Input element
   * @param resetButton - Reset button element
   */
  protected handleReset(input: HTMLInputElement, resetButton: HTMLElement): void {
    input.value = '';
    this.state.currentQuery = '';
    this.state.keyboardNavIndex = -1;

    // Hide reset button
    resetButton.classList.add('boost-sd__g-hide');

    // Sync all inputs
    this.syncAllInputs('');

    // Render default data
    this.renderDefaultData();

    // Focus input
    input.focus();
  }

  /**
   * Handle submit button keydown for WCAG keyboard activation tracking.
   *
   * **Legacy**: Inline keydown handler on submit button in initInstantSearchWidgetEvents()
   * **New**: Dedicated method for extensibility
   *
   * @param event - Keyboard event
   * @param button - Submit button element
   */
  protected handleSubmitButtonKeydown(event: KeyboardEvent, button: HTMLElement): void {
    if (ACTIVATION_KEYS.includes(event.key)) {
      button.classList.add('keyboard-activated');
      sessionStorage.setItem('keyboardActivatedSearch', 'true');
    }
  }

  /**
   * Open suggestion dropdown/overlay.
   *
   * **Legacy**: openSuggestion() / onOpenSuggestion()
   * **New**: openSuggestion(input) with state management
   *
   * @param input - Input element to open suggestions for
   */
  async openSuggestion(input: HTMLInputElement): Promise<void> {
    // Prevent reopening during an active close cycle (e.g., restoreFocus → focusin)
    if (this.isClosing) return;

    if (this.state.isOpen && this.state.activeInputId === input.id) {
      return; // Already open for this input
    }

    // When ISW is already open and the focus came from the ISW's own search bar
    // (e.g., openFullWidth called searchBarInput.focus() after 200ms), just update
    // the tracked input and return — do NOT call openFullWidth again (infinite loop).
    if (this.state.isOpen && input.closest('.boost-sd__instant-search-container')) {
      this.state.activeInputId = input.id;
      return;
    }

    // Save focus element for restoration
    this.saveFocusElement(input);

    const searchSettings = this.searchSettings!;
    const isFullWidth = this.instantSearchSelectors.isFullWidthStyle(searchSettings);

    if (isFullWidth) {
      await this.openFullWidth(input);
    } else {
      await this.openDropdown(input);
    }

    this.state.isOpen = true;
    this.state.activeInputId = input.id;
    input.setAttribute('aria-expanded', 'true');
  }

  /**
   * Open full-width style overlay.
   *
   * @param input - Input element
   */
  protected async openFullWidth(input: HTMLInputElement): Promise<void> {
    // Close theme search overlays with custom selector from general settings
    const generalSettings = (this.appService.TAEAppConfig as unknown as Record<string, unknown>)
      ?.generalSettings as Record<string, string> | undefined;
    this.instantSearchSelectors.closeThemeSearchOverlays(generalSettings?.closeSearchThemeSelector);

    // Hide theme dialogs (z-index conflict prevention)
    this.instantSearchSelectors.hideThemeDialog();

    // Show the suggestion panel — legacy selector: container > div
    const suggestionPanel = this.instantSearchSelectors.getSuggestionWrapper();
    if (suggestionPanel) {
      requestAnimationFrame(() => {
        suggestionPanel.classList.remove('boost-sd__g-hide');
        suggestionPanel.setAttribute('aria-controls', input.id);
      });
    }

    // Add opening class (same as dropdown — legacy adds for both styles)
    requestAnimationFrame(() => {
      document.body.classList.add('boost-sd__search-opening');
    });

    // Focus the ISW's own search bar input (.boost-sd__search-bar-input).
    const isMobile = this.appService.detectDeviceByWidth().includes('tablet_portrait_max');
    const containerSel = this.instantSearchSelectors.getContainerSelector(isMobile);
    const searchBarInput = document.querySelector<HTMLInputElement>(
      `${containerSel} .boost-sd__search-bar-input`
    );
    if (searchBarInput) {
      // - preventInfiniteISW flag is set (app config), OR
      //   → focal/prestige themes: immediate focus causes infinite error loop (PFSN-43210)
      const preventInfiniteISW = (generalSettings as Record<string, unknown>)?.preventInfiniteISW;
      const searchElement = document.querySelector(
        '.header__icon-wrapper, .header__nav-icon.icon-search'
      );
      if (isMobile || preventInfiniteISW || searchElement) {
        this.focusDelayTimer = setTimeout(() => {
          this.focusDelayTimer = null;
          searchBarInput.focus();
        }, 200);
      } else {
        requestAnimationFrame(() => {
          searchBarInput.focus();
        });
      }
      document.body.classList.add('boost-sd__g-no-scroll');
    }

    // Trap focus inside overlay
    this.activateFocusTrap();
  }

  /**
   * Open dropdown style.
   *
   * @param input - Input element
   */
  protected async openDropdown(input: HTMLInputElement): Promise<void> {
    // Wait for CSS animations to finish (e.g., themes where search input animates into view)
    // Legacy: only awaits if input is NOT inside the ISW container and NOT full-width
    const isOwnerInput = input.closest('.boost-sd__instant-search-container');
    if (!isOwnerInput) {
      await this.awaitAnimationElementFinished(input);
    }

    // Calculate and apply position BEFORE showing wrapper to avoid flash
    // Legacy: calcPositionSuggestionResult runs before onOpenSuggestion
    this.updatePosition(input);

    // Show the suggestion panel — legacy selector: container > div
    const suggestionPanel = this.instantSearchSelectors.getSuggestionWrapper();
    if (suggestionPanel) {
      requestAnimationFrame(() => {
        suggestionPanel.classList.remove('boost-sd__g-hide');
        suggestionPanel.setAttribute('aria-controls', input.id);
      });
    }

    // Add opening class when input is not already a boost-sd input (legacy behavior)
    if (!input.classList.contains('boost-sd__search-form-input')) {
      requestAnimationFrame(() => {
        document.body.classList.add('boost-sd__search-opening');
      });
    }
  }

  /**
   * Close suggestion dropdown/overlay.
   *
   * **Legacy**: closeSuggestion() / onCloseSuggestion()
   * **New**: closeSuggestion(input?) with state management
   *
   * @param input - Optional input element
   */
  closeSuggestion(input?: HTMLInputElement): void {
    if (!this.state.isOpen) return;

    this.isClosing = true;

    const searchSettings = this.searchSettings!;
    const isFullWidth = this.instantSearchSelectors.isFullWidthStyle(searchSettings);

    // Symmetry theme: remove show-search class first, then delay hide by 200ms
    if (this.instantSearchSelectors.isSymmetrySearchOpen()) {
      this.instantSearchSelectors.dismissSymmetrySearch();
      setTimeout(() => {
        this.performClose(isFullWidth, input);
        // Reset guard after Symmetry deferred close completes
        setTimeout(() => {
          this.isClosing = false;
        }, 0);
      }, 200);
      return;
    }

    this.performClose(isFullWidth, input);

    // Reset guard after current microtask so focusin handlers see it during close
    setTimeout(() => {
      this.isClosing = false;
    }, 0);
  }

  /**
   * Perform the actual close operation.
   *
   * Separated from closeSuggestion to support deferred close (e.g., Symmetry theme delay).
   *
   * @param isFullWidth - Whether full-width style is active
   * @param input - Optional input element
   */
  protected performClose(isFullWidth: boolean, input?: HTMLInputElement): void {
    if (isFullWidth) {
      this.closeFullWidth();
    } else {
      this.closeDropdown();
    }

    this.state.isOpen = false;
    this.state.keyboardNavIndex = -1;

    // Update aria-expanded on the input
    const targetInput = input || this.getActiveInput();
    targetInput?.setAttribute('aria-expanded', 'false');

    // Clear aria-activedescendant so screen readers don't announce a stale selection
    targetInput?.removeAttribute('aria-activedescendant');

    // Release focus trap (full-width mode)
    this.deactivateFocusTrap();

    // Clear saved focus element — no auto-restore on close.
    // Legacy never restores focus on close. For Escape key, handleKeydown
    // already calls input.focus() directly after closeSuggestion().
    // Auto-restoring here would trigger focusin → openSuggestion → reopen.
    this.savedFocusElement = null;
  }

  /**
   * Close full-width overlay.
   *
   * Legacy onCloseSuggestion does NOT differentiate full-width vs dropdown —
   * it always hides the same `container > div` element and calls restoreThemeDialog.
   */
  protected closeFullWidth(): void {
    // Cancel any pending delayed focus so it can't reopen the ISW after close
    if (this.focusDelayTimer) {
      clearTimeout(this.focusDelayTimer);
      this.focusDelayTimer = null;
    }
    this.hideContainerPanel();
  }

  /**
   * Close dropdown style.
   */
  protected closeDropdown(): void {
    this.hideContainerPanel();

    // Cleanup scroll handler
    if (this.scrollCleanup) {
      this.scrollCleanup();
      this.scrollCleanup = null;
    }
  }

  /**
   * Hide the suggestion panel and clean up body classes.
   *
   * Matches legacy `onCloseSuggestion` exactly:
   * - Adds `boost-sd__g-hide` to the `container > div` element
   * - Clears `aria-controls`
   * - Removes body classes
   * - Always calls `restoreThemeDialog`
   */
  protected hideContainerPanel(): void {
    const suggestionPanel = this.instantSearchSelectors.getSuggestionWrapper();
    if (suggestionPanel) {
      requestAnimationFrame(() => {
        suggestionPanel.classList.add('boost-sd__g-hide');
        suggestionPanel.setAttribute('aria-controls', '');
        document.body.classList.remove('boost-sd__g-no-scroll');
        document.body.classList.remove('boost-sd__search-opening');
        // Remove inline body styles set by theme <dialog> scroll-lock
        this.cleanupBodyStyles();
        // Always restore theme dialog on close (legacy behavior)
        this.instantSearchSelectors.restoreThemeDialog();
      });
    } else {
      // Fallback: clean up body classes even without a panel
      document.body.classList.remove('boost-sd__g-no-scroll');
      document.body.classList.remove('boost-sd__search-opening');
      this.cleanupBodyStyles();
      this.instantSearchSelectors.restoreThemeDialog();
    }
  }

  /**
   * Remove inline body styles that some themes apply via a native `<dialog>`
   * scroll-lock mechanism (e.g. `position: fixed; top: -scrollY; width: 100%`).
   *
   * These styles are NOT removed by clearing CSS classes, so they must be
   * explicitly stripped as inline properties when ISW closes.
   *
   * Override to restore or preserve specific properties for a given theme.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchController', (InstantSearchController) => {
   *   return class CustomController extends InstantSearchController {
   *     protected cleanupBodyStyles() {
   *       // Also restore scroll position lost by the fixed positioning
   *       const scrollY = parseInt(document.body.style.top || '0', 10);
   *       super.cleanupBodyStyles();
   *       window.scrollTo(0, -scrollY);
   *     }
   *   };
   * });
   * ```
   */
  protected cleanupBodyStyles(): void {
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
  }

  /**
   * Toggle suggestion visibility.
   */
  async toggleSuggestion(): Promise<void> {
    if (this.state.isOpen) {
      this.closeSuggestion();
    } else {
      const inputs = this.instantSearchSelectors.getAllSearchInputs();
      if (inputs.length > 0) {
        await this.openSuggestion(inputs[0]);
      }
    }
  }

  /**
   * Search and render results.
   *
   * @param query - Search query
   */
  protected async searchAndRender(query: string): Promise<void> {
    if (!this.searchSettings) return;

    try {
      this.state.isLoading = true;

      const data = await this.instantSearchService.searchDebounced(query, this.searchSettings);
      await this.render({
        ...this.defaultData!,
        ...data,
        isLoading: false,
      });

      this.state.isLoading = false;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Expected: a newer search cancelled this one via AbortController
      } else {
        this.appService.logger.error('Instant search failed', error);
      }
      this.state.isLoading = false;
    }
  }

  /**
   * Render default data.
   */
  protected async renderDefaultData(): Promise<void> {
    if (this.defaultData) {
      await this.render(this.defaultData);
    }
  }

  /**
   * Render suggestion data.
   *
   * **Legacy**: replaceHTML() + render logic in handleInstantSearchWidget
   * **New**: render(data) with concurrent render prevention
   *
   * @param data - Suggestion data to render
   */
  async render(data: SuggestionData): Promise<void> {
    if (this.isRendering) {
      this.appService.logger.warn('Instant search render already in progress');
      return;
    }

    this.isRendering = true;

    try {
      const rawData = data as unknown as Record<string, unknown>;

      // Escape HTML in all fields that may be rendered in the Liquid template
      const escapeHtml = (s: string) =>
        s
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');

      for (const key of [
        'term',
        'query',
        'correct_query',
        'did_you_mean',
        'suggestions',
      ] as const) {
        const v = rawData[key];
        if (typeof v === 'string') rawData[key] = escapeHtml(v);
        else if (Array.isArray(v))
          rawData[key] = v.map((i) => (typeof i === 'string' ? escapeHtml(i) : i));
      }

      const renderData = this.processWidgetDataForRendering(data);
      const html = await this.instantSearchService.renderSuggestions(this.template, renderData);

      if (html) {
        this.replaceHTML(html);
        this.setupSuggestionClickHandlers();
        this.render3rdAppSupportButton();

        // Announce result count to screen readers
        const productCount = (renderData as { products?: unknown[] }).products?.length ?? 0;
        this.announceResults(productCount);

        // Re-bind search inputs for dynamically added inputs after render
        this.bindSearchInputs();

        // Recalculate position after content change
        if (this.state.isOpen) {
          const input = this.getActiveInput();
          if (input && this.searchSettings) {
            const isFullWidth = this.instantSearchSelectors.isFullWidthStyle(this.searchSettings);
            if (!isFullWidth) {
              this.updatePosition(input);
            }
          }
        }

        // Dispatch rendered event with the (possibly transformed) render data
        this.dispatchRenderedEvent(renderData);
      }
    } catch (error) {
      this.appService.logger.error('Instant search render failed', error);
    } finally {
      this.isRendering = false;
    }
  }

  /**
   * Announce search result count to screen readers via an ARIA live region.
   *
   * Creates a visually-hidden `role="status"` live region on first call and
   * reuses it on subsequent calls. The text is cleared then set so that
   * repeated identical counts still trigger an announcement.
   *
   * **WCAG**: SC 4.1.3 Status Messages (Level AA)
   *
   * @param count - Number of products found
   */
  protected announceResults(count: number): void {
    let liveRegion = document.getElementById(LIVE_REGION_ID);

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = LIVE_REGION_ID;
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      // Visually hidden but accessible to screen readers
      liveRegion.style.cssText =
        'position:absolute;width:1px;height:1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
      document.body.appendChild(liveRegion);
    }

    // Clear then set to ensure the announcement fires even for the same count
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      if (liveRegion) {
        liveRegion.textContent =
          count === 0 ? 'No results found' : `${count} result${count === 1 ? '' : 's'} found`;
      }
    });
  }

  /**
   * Collect all keyboard-focusable elements inside the full-width overlay.
   *
   * @returns Array of focusable elements in DOM order
   */
  protected getFocusTrapElements(): HTMLElement[] {
    const wrapper = document.querySelector<HTMLElement>('.boost-sd__search-widget-init-wrapper');
    if (!wrapper) return [];

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(wrapper.querySelectorAll<HTMLElement>(focusableSelectors)).filter(
      (el) => !el.classList.contains('boost-sd__g-hide') && el.offsetParent !== null
    );
  }

  /**
   * Activate focus trap for full-width overlay mode.
   *
   * Prevents Tab / Shift+Tab from leaving the overlay, keeping keyboard focus
   * cycling through interactive elements inside the ISW wrapper.
   *
   * Stores the handler in `focusTrapHandler` so it can be removed by
   * `deactivateFocusTrap()` without leaking event listeners.
   *
   * **WCAG**: SC 2.1.2 No Keyboard Trap — trap is intentional UX (modal-like
   * overlay) and Escape always closes it, satisfying the escape-mechanism
   * requirement.
   */
  protected activateFocusTrap(): void {
    this.deactivateFocusTrap();

    const handler = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;

      const focusable = this.getFocusTrapElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: wrap from first → last
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: wrap from last → first
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handler);
    this.focusTrapHandler = handler;
  }

  /**
   * Deactivate focus trap, removing the keyboard event listener.
   */
  protected deactivateFocusTrap(): void {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  /**
   * Replace HTML in ISW container.
   *
   * **Legacy**: replaceHTML(context, htmlString)
   * **New**: replaceHTML(htmlString) method
   *
   * @param htmlString - HTML to inject
   */
  protected replaceHTML(htmlString: string): void {
    const container = document.querySelector('.boost-sd__instant-search-container');
    if (!container) {
      // First render: inject full HTML into the ISW element created by TAE Search module.
      // Legacy: context.document.innerHTML = html  (where context.document = #bc-instant-search)
      const iswElement = document.getElementById('bc-instant-search');
      if (iswElement) {
        iswElement.innerHTML = htmlString;
      } else {
        // Fallback: create ISW element if TAE hasn't created it yet
        const wrapper = document.createElement('div');
        wrapper.id = 'bc-instant-search';
        wrapper.innerHTML = htmlString;
        document.body.appendChild(wrapper);
      }
      return;
    }

    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(htmlString, 'text/html');

    // Replace mobile block
    this.replaceBlock(
      container,
      htmlDoc,
      '.boost-sd__instant-search-container-mobile .boost-sd__instant-search-results'
    );

    // Replace desktop block
    this.replaceBlock(
      container,
      htmlDoc,
      '.boost-sd__instant-search-container-desktop .boost-sd__instant-search-results'
    );
  }

  /**
   * Replace a block in the container.
   *
   * @param container - Container element
   * @param htmlDoc - Parsed HTML document
   * @param selector - Block selector
   */
  protected replaceBlock(container: Element, htmlDoc: Document, selector: string): void {
    const oldBlock = container.querySelector(selector);
    const newBlock = htmlDoc.querySelector(selector);

    if (oldBlock && newBlock) {
      oldBlock.parentNode?.replaceChild(newBlock, oldBlock);
    }
  }

  /**
   * Setup click handlers on suggestion items.
   */
  protected setupSuggestionClickHandlers(): void {
    const suggestions = document.querySelectorAll<HTMLElement>(
      '.boost-sd__suggestion-queries-item'
    );

    suggestions.forEach((suggestion) => {
      suggestion.addEventListener('click', () => {
        const title = suggestion.getAttribute('data-title');
        if (title) {
          this.recentSearchService.addRecentSearch(title);

          // Save clicked suggestion term for AIML analytics tracking
          if (suggestion.classList.contains('boost-sd__suggestion-queries-item--suggestion')) {
            this.saveClickedSuggestionTerm(title);
          }
        }
      });
    });
  }

  /**
   * Save clicked suggestion term to localStorage for AIML analytics.
   *
   * The collection-filter module reads this value to attach `suggestion`,
   * `parent_request_id`, and `item_rank` parameters to search API requests.
   *
   * **Legacy**: setLocalStorage(CLICK_SUGGESTION_TERM, title) in render callback
   * **New**: Dedicated method for extensibility and testability
   *
   * @param term - Suggestion term that was clicked
   */
  protected saveClickedSuggestionTerm(term: string): void {
    try {
      localStorage.setItem(CLICK_SUGGESTION_TERM_KEY, term);
    } catch {
      // localStorage may be unavailable (private browsing, storage full, etc.)
    }
  }

  /**
   * Setup global event listeners.
   */
  protected setupGlobalEventListeners(): void {
    // Document click - close on outside click
    const handleDocumentClick = (e: Event) => {
      if (!this.state.isOpen) return;

      // Full-width mode covers the viewport — close only via close button or
      // Escape key. Legacy onClickOutSideSuggestion returns early for
      // isStyleFullWidth.
      if (
        this.searchSettings &&
        this.instantSearchSelectors.isFullWidthStyle(this.searchSettings)
      ) {
        return;
      }

      const target = e.target as HTMLElement;

      // Don't close ISW when clicking inside a bundle modal (dynamic or volume)
      if (target.closest('.boost-sd__modal--in-dialog') || target.closest('.boost-sd__modal')) {
        return;
      }

      const iswContainer = target.closest(
        '.boost-sd__instant-search-container,' +
          ' .boost-sd__instant-search-container-mobile,' +
          ' .boost-sd__instant-search-container-desktop,' +
          ' .boost-sd__search-widget-init-wrapper'
      );
      const isSearchInput = target.closest('.boost-sd__search-widget-init-input');
      const isClickHeaderIconSearch = target.closest('.header__icon--search');
      const isClickWatermark = target.closest('.boost-sd__watermark-powered-by-boost');

      if (!iswContainer && !isSearchInput && !isClickHeaderIconSearch) {
        this.closeSuggestion();
        return;
      }

      const activeInput = this.getActiveInput();
      const hasSearchModal = activeInput?.closest('.search-modal');
      const isIswInDialog = this.positionService.isISWInDialog();

      if (iswContainer && hasSearchModal && !isIswInDialog) {
        // Exception: clicking the "Powered by Boost" watermark keeps ISW open
        if (isClickWatermark) return;
        this.closeSuggestion();
      }
    };

    document.addEventListener('click', handleDocumentClick, true);
    this.boundHandlers.set('documentClick', handleDocumentClick);

    const handleResize = async () => {
      if (!this.searchSettings) return;

      const isMobile = this.appService.detectDeviceByWidth().includes('tablet_portrait_max');

      if (this.previousIsMobile !== null && this.previousIsMobile !== isMobile) {
        // Close ISW and reset selection on breakpoint change
        this.state.keyboardNavIndex = -1;
        const input = this.getActiveInput();
        if (input) {
          this.removeCurrentSelected();
        }
        this.closeSuggestion();
        this.previousIsMobile = isMobile;
        return;
      }

      this.previousIsMobile = isMobile;

      // Same breakpoint — recalculate position if open
      if (!this.state.isOpen) return;

      const input = this.getActiveInput();
      if (!input) return;

      const isFullWidth = this.instantSearchSelectors.isFullWidthStyle(this.searchSettings);
      if (!isFullWidth) {
        await this.awaitAnimationElementFinished(input);
        this.updatePosition(input);
      }
    };

    const debouncedResize = this.debounce(handleResize, 100);
    window.addEventListener('resize', debouncedResize as EventListener);
    this.boundHandlers.set('resize', debouncedResize as EventListener);
  }

  /**
   * Setup all event listeners: global (document click, resize) + custom window events.
   *
   * All handlers are stored in `boundHandlers` so `destroy()` can clean them up.
   */
  protected setupEventListeners(): void {
    this.setupGlobalEventListeners();

    // --- Custom window events ---

    // Open ISW event
    const handleOpenISW = ((e: CustomEvent) => {
      const input = e.detail?.input || this.getActiveInput();
      if (input) {
        this.openSuggestion(input);
      }
    }) as EventListener;
    window.addEventListener('boost-sd-open-isw', handleOpenISW);
    this.boundHandlers.set('boost-sd-open-isw', handleOpenISW);

    // Close ISW event
    const handleCloseISW = (): void => {
      this.closeSuggestion();
    };
    window.addEventListener('boost-sd-close-isw', handleCloseISW);
    this.boundHandlers.set('boost-sd-close-isw', handleCloseISW);

    // Search event
    const handleSearch = ((e: CustomEvent) => {
      const query = e.detail?.query;
      if (query && this.searchSettings) {
        this.searchAndRender(query);
      }
    }) as EventListener;
    window.addEventListener('boost-sd-search', handleSearch);
    this.boundHandlers.set('boost-sd-search', handleSearch);

    // Bind search inputs event
    const handleBindSearchInput = (): void => {
      this.bindSearchInputs();
    };
    window.addEventListener('bind-search-input', handleBindSearchInput);
    this.boundHandlers.set('bind-search-input', handleBindSearchInput);
  }

  /**
   * Handle dialog integration for dropdown styles.
   *
   * For dropdown styles, observes the theme's `#search-modal` host element and
   * injects ISW into the dialog when it opens, then moves it back to the body
   * when the dialog closes.  Full-width styles skip this entirely — the ISW
   * overlay already covers the viewport.
   *
   * **Legacy**: handleDialogIntegrationForDropdownStyles()
   * **New**: handleDialogIntegration() + injectISWIntoDialog() + moveISWBackToBody()
   */
  protected handleDialogIntegration(): void {
    if (!this.searchSettings) return;

    const isFullWidth = this.instantSearchSelectors.isFullWidthStyle(this.searchSettings);

    // Legacy: full-width simply returns early — no dialog observer needed.
    // Full-width ISW opens via input focusin/click events, not dialog mutations.
    if (isFullWidth) return;

    const searchModalHost = document.getElementById('search-modal');
    if (!searchModalHost) return;

    let hasInserted = false;

    const observer = new MutationObserver(async () => {
      const dialog = searchModalHost.querySelector<HTMLElement>('dialog, [data-ref="dialog"]');
      if (!dialog) return;

      // For native <dialog>, use the .open property — it is reliable even when
      // the parent (#search-modal) is display:none, because showModal() places
      // the dialog on the browser's top layer above all CSS stacking contexts.
      // getComputedStyle can falsely report 'none' in that scenario.
      const isOpen =
        dialog instanceof HTMLDialogElement
          ? dialog.open
          : dialog.hasAttribute('open') && window.getComputedStyle(dialog).display !== 'none';

      if (isOpen && !hasInserted) {
        const inserted = await this.injectISWIntoDialog(dialog);
        // Double-guard after await: only mark inserted if we weren't already
        if (inserted && !hasInserted) {
          hasInserted = true;
        }
      } else if (!isOpen && hasInserted) {
        this.moveISWBackToBody(dialog);
        hasInserted = false;
      }
    });

    observer.observe(searchModalHost, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });

    this.dialogObserver = observer;
  }

  /**
   * Inject the ISW element into a theme search dialog.
   *
   * Waits for the ISW root element and the predictive-search container inside
   * the dialog to be available, then inserts ISW immediately after the
   * predictive-search container and sizes the dialog to fit.
   *
   * @param dialog - The open dialog element to inject ISW into
   * @returns `true` if ISW was successfully injected, `false` otherwise
   */
  protected async injectISWIntoDialog(dialog: HTMLElement): Promise<boolean> {
    const iswElement = await this.instantSearchSelectors.waitForISWElement();
    if (!iswElement) return false;

    const predictiveSearch =
      await this.instantSearchSelectors.waitForPredictiveSearchContainer(dialog);
    if (!predictiveSearch) return false;

    iswElement.dataset.boostMovedToDialog = 'true';
    predictiveSearch.insertAdjacentElement('afterend', iswElement);

    requestAnimationFrame(() => {
      const iswRect = iswElement.getBoundingClientRect();
      dialog.style.height = `${iswRect.height}px`;
      dialog.style.overflow = 'visible';
    });

    return true;
  }

  /**
   * Move the ISW element back to `document.body` after a dialog closes.
   *
   * Restores dialog dimensions and resets wrapper position styles so that
   * subsequent dropdown-style positioning calculations start from a clean state.
   *
   * @param dialog - The dialog element that was hosting ISW
   */
  protected moveISWBackToBody(dialog: HTMLElement): void {
    const iswElement = this.instantSearchSelectors.getISWElement();
    if (!iswElement || iswElement.dataset.boostMovedToDialog !== 'true') return;

    delete iswElement.dataset.boostMovedToDialog;
    if (iswElement.parentElement !== document.body) {
      document.body.appendChild(iswElement);
    }

    dialog.style.height = '';
    dialog.style.overflow = '';

    const wrapper = this.instantSearchSelectors.getSuggestionWrapper();
    if (wrapper) {
      requestAnimationFrame(() => {
        wrapper.style.position = '';
        wrapper.style.top = '';
        wrapper.style.left = '';
        wrapper.style.right = '';
        wrapper.style.zIndex = '';
      });
    }
  }

  /**
   * Get suggestion items.
   *
   * @returns Array of suggestion item elements
   */
  protected getSuggestionItems(): HTMLElement[] {
    const items = document.querySelectorAll<HTMLElement>(
      '.boost-sd__suggestion-queries-item:not(.boost-sd__suggestion-queries-item--label)'
    );
    return Array.from(items);
  }

  /**
   * Get all keyboard-navigable items including interactive buttons.
   *
   * Returns items in navigation order: clear button, close button,
   * suggestion items, then view-all products button.
   *
   * **Legacy**: onKeyboardNavigation() counted these separately with index offsets
   * **New**: Single ordered array for clean navigation
   *
   * @returns Array of navigable elements in order
   */
  protected getNavigableItems(): HTMLElement[] {
    const items: HTMLElement[] = [];
    const container = document.querySelector('.boost-sd__instant-search-container');
    if (!container) return this.getSuggestionItems();

    // Clear button (if visible)
    const clearButton = container.querySelector<HTMLElement>(
      ':not(.boost-sd__g-hide)[data-group="clear-search-form"]'
    );
    if (clearButton) items.push(clearButton);

    // Close button
    const closeButton = container.querySelector<HTMLElement>(
      '.boost-sd__button-text-x--close[data-group="close-search-form"]'
    );
    if (closeButton) items.push(closeButton);

    // Suggestion items
    items.push(...this.getSuggestionItems());

    // View all products button
    const viewAll = container.querySelector<HTMLElement>('[data-group="view-all-products"]');
    if (viewAll) items.push(viewAll);

    return items;
  }

  /**
   * Get active input element.
   *
   * @returns Active input or first input
   */
  protected getActiveInput(): HTMLInputElement | null {
    if (this.state.activeInputId) {
      return document.getElementById(this.state.activeInputId) as HTMLInputElement;
    }

    const inputs = this.instantSearchSelectors.getAllSearchInputs();
    return inputs[0] || null;
  }

  /**
   * Sync value across all search inputs.
   *
   * @param value - Value to sync
   */
  protected syncAllInputs(value: string): void {
    const inputs = this.instantSearchSelectors.getAllSearchInputs();
    inputs.forEach((input) => {
      if (input.id !== this.state.activeInputId) {
        input.value = value;
      }
    });
  }

  /**
   * Toggle reset button visibility.
   *
   * @param input - Input element
   * @param show - Whether to show
   */
  protected toggleResetButton(input: HTMLInputElement, show: boolean): void {
    const form = input.closest('form');
    const resetButton = form?.querySelector(
      '[type="reset"], .reset__button, .boost-sd__button-text-x--clear'
    );

    if (resetButton) {
      resetButton.classList.toggle('boost-sd__g-hide', !show);
    }
  }

  /**
   * Remove current selection.
   */
  protected removeCurrentSelected(): void {
    const selected = document.querySelector(`.${SELECTED_CLASS}`);
    if (selected) {
      selected.classList.remove(SELECTED_CLASS);
      selected.setAttribute('aria-selected', 'false');
    }
  }

  /**
   * Save focus element for restoration.
   *
   * @param element - Element to save
   */
  protected saveFocusElement(element: HTMLElement): void {
    this.savedFocusElement = element;
  }

  /**
   * Restore saved focus element.
   */
  protected restoreFocusedElement(): void {
    if (this.savedFocusElement) {
      this.savedFocusElement.focus();
      this.savedFocusElement = null;
    }
  }

  /**
   * Dispatch rendered event.
   *
   * @param data - Rendered data
   */
  protected dispatchRenderedEvent(data: SuggestionData): void {
    const bundles = (data as SuggestionData & { bundles?: Array<Record<string, unknown>> }).bundles;
    this.currentBundles = Array.isArray(bundles) ? bundles : [];

    window.dispatchEvent(
      new CustomEvent('boost-sd-isw-rendered', {
        detail: { data },
      })
    );
  }

  /**
   * Strip HTML tags from string.
   *
   * @param str - String to strip
   * @returns Stripped string
   */
  protected stripHtml(str: string): string {
    if (!str) return '';

    const doc = new DOMParser().parseFromString(str, 'text/html');
    return doc.body.textContent || '';
  }
  /**
   * Check whether a string contains HTML tags.
   *
   * @param str - String to check
   * @returns `true` if the string contains HTML markup
   */
  protected containsHtml(str: string): boolean {
    return /<[a-z/!][^>]*>/i.test(str);
  }
  /**
   * Simple debounce utility.
   *
   * @param func - Function to debounce
   * @param wait - Wait time in ms
   * @returns Debounced function
   */
  protected debounce<T extends (...args: unknown[]) => unknown>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<T>) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  /**
   * Update ISW dropdown position for a given input.
   *
   * Convenience method wrapping the PositionService.updatePosition flow.
   * Manages scroll handler cleanup automatically.
   *
   * **Legacy**: calcPositionSuggestionResult(context, input)
   * **New**: updatePosition(input) with lifecycle management
   *
   * @param input - The search input element to position relative to
   */
  protected updatePosition(input: HTMLInputElement): void {
    if (!this.searchSettings) return;

    const container = this.positionService.getContainerElement();
    if (!container) return;

    // Cleanup previous scroll handler
    if (this.scrollCleanup) {
      this.scrollCleanup();
      this.scrollCleanup = null;
    }

    this.scrollCleanup = this.positionService.updatePosition(input, container, this.searchSettings);
  }

  /**
   * Wait for an element's CSS animation to finish by polling its position.
   *
   * Polls the element's `getBoundingClientRect()` at intervals. Once the
   * x/y coordinates stabilize between two consecutive polls (and are >= 0),
   * the animation is considered finished.
   *
   * **Legacy**: `awaitAnimationElementFinished(htmlElement, step, maxMs)`
   * **New**: Instance method with same signature and behavior
   *
   * @param element - The element to watch
   * @param step - Polling interval in ms (default 100)
   * @param maxMs - Maximum wait time in ms (default 1000)
   */
  protected async awaitAnimationElementFinished(
    element: HTMLElement,
    step: number = 100,
    maxMs: number = 1000
  ): Promise<void> {
    if (!element) return;

    const positions: Array<{ x: number; y: number }> = [];
    const maxLoop = Math.floor(maxMs / step);

    for (let i = 0; i < maxLoop; i++) {
      const rect = element.getBoundingClientRect();
      positions.push({ x: rect.x, y: rect.y });

      if (
        i > 0 &&
        positions[i].x >= 0 &&
        positions[i].y >= 0 &&
        positions[i].x === positions[i - 1].x &&
        positions[i].y === positions[i - 1].y
      ) {
        break;
      }

      await new Promise<void>((resolve) => setTimeout(resolve, step));
    }
  }

  /**
   * Ensure the mobile search bar wrapper is visible.
   *
   * Some themes initially hide the mobile wrapper. This method removes
   * the hide class on user interaction (input, click, focus).
   *
   * **Legacy**: `ensureMobileWrapperVisible()` inline closure
   * **New**: Dedicated method on controller
   */
  protected ensureMobileWrapperVisible(): void {
    const wrapper = document.querySelector<HTMLElement>('.boost-sd__search-bar-wrapper--mobile');
    if (wrapper?.classList.contains('boost-sd__g-hide')) {
      wrapper.classList.remove('boost-sd__g-hide');
    }
  }

  /**
   * Process suggestion data before passing it to the template renderer.
   *
   * Extension point for TS teams to modify, filter, or augment suggestion data
   * immediately before rendering — without overriding the entire `render()` method.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchController', (InstantSearchController) => {
   *   return class CustomController extends InstantSearchController {
   *     protected processWidgetDataForRendering(data) {
   *       // Cap product count to 3 for this shop
   *       return { ...data, products: (data.products ?? []).slice(0, 3) };
   *     }
   *   };
   * });
   * ```
   *
   * @param data - Suggestion data about to be rendered
   * @returns Data to pass to the template (identity by default)
   */
  protected processWidgetDataForRendering(data: SuggestionData): SuggestionData {
    return data;
  }

  /**
   * Detect the mobile ISW style from the rendered DOM.
   * - **style1** (full-width overlay): mobile container contains `.boost-sd__search-bar-wrapper--mobile`
   * - **style2** (dropdown): no `.boost-sd__search-bar-wrapper--mobile` present
   *
   * Override to customise detection for non-standard Liquid template structures.
   *
   * @returns `'style1'`, `'style2'`, or `null` if the mobile container is not yet in the DOM
   */
  protected detectMobileStyleFromDOM(): 'style1' | 'style2' | null {
    const mobileContainer = document.querySelector('.boost-sd__instant-search-container-mobile');
    if (!mobileContainer) return null;
    return mobileContainer.querySelector('.boost-sd__search-bar-wrapper--mobile')
      ? 'style1'
      : 'style2';
  }

  /**
   * Apply widget styles to the ISW container element.
   *
   * Called once during `initWithContext()` after the initial render so the
   * container element exists in the DOM. Override to apply shop-specific
   * inline CSS custom properties or extra class names.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchController', (InstantSearchController) => {
   *   return class CustomController extends InstantSearchController {
   *     protected applyWidgetStyles(container) {
   *       super.applyWidgetStyles(container);
   *       // Tint the search overlay for this brand
   *       container.style.setProperty('--boost-primary-color', '#ff6600');
   *     }
   *   };
   * });
   * ```
   *
   * @param container - The ISW container element (or `document.body` as fallback)
   */
  protected applyWidgetStyles(container: HTMLElement): void {
    const styles = this.getShopSpecificStyles();
    for (const [property, value] of Object.entries(styles)) {
      container.style.setProperty(property, value);
    }
  }

  /**
   * Get shop-specific CSS custom properties to inject into the widget container.
   *
   * Override to provide per-shop theming without touching global CSS.
   * The returned object is applied via `container.style.setProperty()`, so
   * keys must be valid CSS custom property names (e.g. `--boost-isw-max-width`).
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchController', (InstantSearchController) => {
   *   return class CustomController extends InstantSearchController {
   *     protected getShopSpecificStyles() {
   *       return {
   *         '--boost-isw-max-width': '640px',
   *         '--boost-isw-border-radius': '12px',
   *       };
   *     }
   *   };
   * });
   * ```
   *
   * @returns Record mapping CSS custom property names to values (empty by default)
   */
  protected getShopSpecificStyles(): Record<string, string> {
    return {};
  }

  /**
   * Close the theme `<dialog>` that is currently hosting the ISW element.
   *
   * Override to apply a custom close strategy for a specific theme
   * (e.g. clicking a close button instead of calling `dialog.close()`).
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchController', (InstantSearchController) => {
   *   return class CustomController extends InstantSearchController {
   *     protected closeISWDialog() {
   *       document.querySelector('.my-search-modal__close')?.click();
   *     }
   *   };
   * });
   * ```
   */
  protected closeISWDialog(): void {
    const iswElement = this.instantSearchSelectors.getISWElement();
    if (!iswElement || iswElement.dataset.boostMovedToDialog !== 'true') return;

    const dialog = iswElement.closest<HTMLElement>('dialog, [data-ref="dialog"]');
    if (!dialog) return;

    if (typeof (dialog as HTMLDialogElement).close === 'function') {
      (dialog as HTMLDialogElement).close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  /**
   * Render a 3rd-party support CRM button inside the ISW container.
   *
   * @example
   * ```javascript
   * // Example 1: call super then add extra behaviour
   * window.boostWidgetIntegration.extend('InstantSearchController', (InstantSearchController) => {
   *   return class CustomController extends InstantSearchController {
   *     protected render3rdAppSupportButton() {
   *       super.render3rdAppSupportButton();
   *     }
   *   };
   * });
   *
   * // Example 2: style the rendered button per-shop
   * window.boostWidgetIntegration.extend('InstantSearchController', (InstantSearchController) => {
   *   return class CustomController extends InstantSearchController {
   *     protected render3rdAppSupportButton() {
   *       super.render3rdAppSupportButton();
   *       const button = document.querySelector('.boost-sd__support-button');
   *       if (!button) return;
   *       button.classList.add('my-custom-class');
   *     }
   *   };
   * });
   * ```
   */
  protected render3rdAppSupportButton(): void {
    const supportCRMKey = 'supportCRM';
    const SUPPORT_BUTTON_CONTAINER_CLASS = 'boost-sd__support-button-wrapper';
    const SUPPORT_BUTTON_CLASS = 'boost-sd__support-button';

    const currentAppIntegration = this.appService.currentAppIntegration as Record<string, unknown>;
    const appName = currentAppIntegration[supportCRMKey] as TCustomerSupportProvider;
    if (!appName) return;

    const extraFeatures = (currentAppIntegration.extraFeatures || {}) as Record<
      string,
      { settings?: Record<string, unknown>; isEnabled?: boolean }
    >;
    const { settings = {}, isEnabled = false } = extraFeatures[appName] || {};
    if (!isEnabled || !settings) return;

    const iswContainer = document.querySelector<HTMLElement>('.boost-sd__instant-search-container');
    if (!iswContainer) return;

    const supportButtonContainer = iswContainer.querySelector<HTMLElement>(
      `.${SUPPORT_BUTTON_CONTAINER_CLASS}`
    );

    if (!supportButtonContainer || supportButtonContainer.querySelector(`.${SUPPORT_BUTTON_CLASS}`))
      return;

    const callback = () => {
      this.positionService.isISWInDialog() ? this.closeISWDialog() : this.closeSuggestion();
    };

    renderSupportButton(
      supportButtonContainer,
      {
        buttonSettings: settings,
        provider: appName,
      },
      callback
    );
  }

  /**
   * Cleanup event listeners and resources.
   */
  destroy(): void {
    this.boundHandlers.forEach((handler, key) => {
      if (key === 'documentClick') {
        document.removeEventListener('click', handler, true);
      } else if (key === 'resize') {
        window.removeEventListener('resize', handler);
      } else {
        // Custom window events: boost-sd-open-isw, boost-sd-close-isw, etc.
        window.removeEventListener(key, handler);
      }
    });
    this.boundHandlers.clear();

    // Cleanup scroll handler
    if (this.scrollCleanup) {
      this.scrollCleanup();
      this.scrollCleanup = null;
    }

    // Cleanup dialog observer
    if (this.dialogObserver) {
      this.dialogObserver.disconnect();
      this.dialogObserver = null;
    }

    // Cleanup position service
    this.positionService.dispose();

    // Cancel any pending delayed focus
    if (this.focusDelayTimer) {
      clearTimeout(this.focusDelayTimer);
      this.focusDelayTimer = null;
    }

    // Remove focus trap if still active
    this.deactivateFocusTrap();
  }
}
